<template>
  <div class="main">
    <FormWrapper :action="formAction" :method="formMethod" autocomplete="off">
      <div class="title-bar">
        <h1 class="mb-0">{{ user.id ? "Edit" : "Add New" }} User</h1>
        <a class="btn-back" :href="route('admin.users.index')">
          <span class="icon icon-arrow-left"></span>
          <span class="text">Back to List</span>
        </a>
      </div>
      <div class="row no-gutters py-4">
        <div class="col-12 col-lg-8 col-xl-9">
          <div class="row no-gutters pl-4">
            <div class="col-12">
              <transition name="fade">
                <Alert v-if="hasAlert" :alert="alert" />
              </transition>
              <h3 class="text-uppercase text-primary mb-3">User Details</h3>
            </div>
            <div class="col-12 mb-3 col-lg-4 pr-lg-4">
              <Field type="text"
                     name="first_name"
                     label="First Name"
                     :value="user.first_name"
                     validate="required"
                     validate-as="first name"
                     :disabled="!isEditable"
                    autocomplete="off" />
            </div>
            <div class="col-12 mb-3 col-lg-8">
              <Field type="text"
                     name="last_name"
                     label="Last Name"
                     :value="user.last_name"
                     validate="required"
                     validate-as="last name"
                     :disabled="!isEditable" />
            </div>
            <div class="col-12 mb-3 col-lg-4 pr-lg-4">
              <Field type="text"
                     name="username"
                     label="Email/Username"
                     :value="user.username"
                     validate="required"
                     autocomplete="off"
                     :readonly="readonly"
                     :disabled="!canEditUsername"
              />
            </div>
            <div class="col-12 mb-3 col-lg-4 pr-lg-4">
              <Field type="text"
                     name="email"
                     label="Email"
                     :value="user.email"
                     :disabled="!canEditEmail" />
            </div>
            <div class="col-12 mb-3 col-lg-4">
              <Field type="text"
                     name="phone"
                     label="Phone"
                     :value="user.phone"
                     :disabled="!isEditable" />
            </div>
            <div class="col-12 mb-3 col-lg-4  pr-lg-4">
              <Field type="text"
                     name="fax"
                     label="Fax"
                     :value="user.fax"
                     :disabled="!isEditable" />
            </div>
            <div class="col-12 col-lg-4 mb-3 pr-lg-4">
              <Field type="text"
                     name="title"
                     label="Title"
                     :value="user.title"
                     :disabled="!isEditable" />
            </div>
            <div v-if="!!user.hvac_id"
                 class="col-12 mb-3 col-lg-4"
                 :class="{ 'pr-lg-4': showAviatorId }">
              <Field type="text"
                     name="hvac_id"
                     label="HVAC ID"
                     :value="user.hvac_id"
                     :disabled="!isEditable" />
            </div>
            <div v-if="showAviatorId" class="col-12 mb-3 col-lg-4">
              <Field type="text"
                     name="aviator_id"
                     label="Aviator ID"
                     :value="user.aviator_id"
                     :disabled="!$root.isSuperUser" />
            </div>
            <div class="col-12 mb-3 col-lg-4 pr-lg-4 pb-lg-0">
              <Field type="checkbox"
                     name="is_active"
                     id="cb_is_active"
                     label="This user is ACTIVE"
                     :value="isActive"
                     :checkbox-value="1"
                     :disabled="!isEditable" />
            </div>
            <div class="col-12 mb-3 col-lg-4 pr-lg-4 pb-lg-0">
              <Field type="checkbox"
                     name="is_writein"
                     label="This user is a WRITE-IN"
                     :value="isWritein"
                     :checkbox-value="1"
                     :disabled="true" />
            </div>
            <div class="col-12 mb-3 col-lg-4 pb-lg-0">
              <Field type="checkbox"
                     name="is_contact"
                     label="This user is a LOCAL CONTACT"
                     :value="!!user.is_contact"
                     :checkbox-value="1"
                     :disabled="true" />
            </div>
            <div class="col-12">
              <hr />
            </div>
            <div class="col-12 mb-3 col-lg-8 pr-lg-4">
              <h3 class="text-uppercase text-primary mb-3">Company</h3>
              <Field v-if="!isEditable"
                     type="text"
                     name="company"
                     label="Company"
                     :value="user.company.name"
                     :disabled="true" />
              <SuggestField v-if="isEditable && user.is_active"
                            name="company_id"
                            label="Search by Company Name"
                            validate="required"
                            :search-url="route('admin.users.company-search')"
                            :value="selectedCompany" />
            </div>
            <div class="col-12 mb-3 col-lg-4">
              <h3 class="text-uppercase text-primary mb-3">Access Level</h3>
              <Field type="select"
                     name="access_level"
                     label="Admin Access"
                     :value="user.access_level"
                     :options="adminAccessOptions"
                     :hideSelectEmptyOption="true" />
            </div>
            <template v-if="!user.id || isEditable">
              <div class="col-12 mb-3">
                <hr />
                <h3 class="text-uppercase text-primary mb-3">Password</h3>
              </div>
              <div class="col-12 col-lg-4">
                <Field type="password"
                       name="password"
                       :readonly="readonly"
                       value=""
                       :placeholder="!!user.id ? 'Leave blank to keep the current password' : ''" />
              </div>
            </template>
            <template v-if="!!user.id && isEditable">
              <div class="col-12">
                <hr />
                <h3 class="text-uppercase text-primary mb-3">
                  Account Registration Details
                </h3>
              </div>
              <div class="col-12 mb-3 col-lg-4 pr-lg-4">
                <Field type="text"
                       name="user_type"
                       label="User Type"
                       :value="userType"
                       :disabled="true" />
              </div>
              <div class="col-12 mb-3 col-lg-4 pr-lg-4">
                <Field type="text"
                       name="hvac_distributor"
                       label="HVAC Distributor"
                       :value="metaValue('hvac_distributor')"
                       :disabled="true" />
              </div>
              <div class="col-12 mb-3 col-lg-4">
                <Field type="text"
                       name="hvac_brand"
                       label="HVAC Brand"
                       :value="metaValue('hvac_brand')"
                       :disabled="true" />
              </div>
              <div class="col-12">
                <Field type="text"
                       name="additional_access"
                       label="Additional Branded Access?"
                       :value="additionalBrandedAccess"
                       :disabled="true" />
              </div>
              <div class="col-12">
                <hr />
                <h3 class="text-uppercase text-primary mb-3">
                  Admin Notes
                </h3>
              </div>
              <div class="col-12">
                <Field type="textarea" name="admin_notes" v-model="user.admin_notes"/>
              </div>
            </template>
            <template v-if="isTrainer">
              <div class="col-12">
                <hr />
                <h3 class="text-uppercase text-primary mb-3">Trainer Bio</h3>
              </div>
              <div class="col-12 mb-3 col-lg-4 pr-lg-4">
                <Field type="text"
                       name="trainer_type"
                       label="Trainer Type"
                       :value="user.trainer_type_name"
                       :disabled="true" />
              </div>
              <div class="col-12 mb-3 col-lg-8 pt-1">
                <label>&nbsp;</label>
                <Field type="checkbox"
                       name="is_authorized_trainer"
                       label="This user can train AUTHORIZED TRAINING courses"
                       :value="!!user.is_authorized_trainer"
                       :checkbox-value="1"
                       :disabled="true" />
              </div>
              <div class="col-12">
                <HtmlField name="meta[bio]" :value="metaValue('bio')" />
              </div>
            </template>
          </div>
        </div>
        <div class="col-12 col-lg-4 col-xl-3 form-button-wrapper">
          <div class="sticky-top">
            <div class="row no-gutters text-center">
              <div class="col-12 pb-2">
                <button type="submit" class="btn btn-success w-50">
                  Save Changes
                </button>
              </div>
              <div class="col-12 py-2">
                <a :href="route('admin.users.index')" class="btn btn-danger w-50">
	                Cancel & Exit
                </a>
              </div>
              <hr v-if="user.id" class="w-75 my-3" />
              <div v-if="user.id && user.company_id" class="col-12 py-2">
                <a :href="route('training.companies.show', { company: user.company_id })"
                   class="btn btn-secondary w-50">
	                Company Details
                </a>
              </div>
              <div v-if="user.id" class="col-12 py-2">
                <a :href="
                    route('admin.users.history.index', {
                      user: user.id,
                      type: 'online',
                    })
                  "
                   class="btn btn-secondary w-50">
	                Training History
                </a>
              </div>
              <div v-if="user.id" class="col-12 py-2">
                <a href="#"
                   @click.prevent="showHoursModal()"
                   class="btn btn-secondary w-50">
	                Hours
                </a>
              </div>
<!--              <div v-if="user.id" class="col-12 py-2">-->
<!--                <a :href="route('admin.users.log', { user: user.id })" class="btn btn-secondary w-50">-->
<!--	                Account History-->
<!--                </a>-->
<!--              </div>-->
              <div v-if="isPortrayable" class="col-12 py-2">
                <a :href="`/?portray=${user.id}`" class="btn btn-secondary w-50">Portray User</a>
              </div>
              <div v-if="user.id" class="col-12 font-size-sm text-center pt-4">
                <span>
	                Last updated on
                  {{ user.updated_at | moment("MM/DD/YYYY") }}
                </span>
                <span>at {{ user.updated_at | moment("h:mma") }}</span>
                <span v-if="user.updatedBy">
	                {{ " " + user.updatedBy.name }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FormWrapper>
  </div>
</template>

<script>
import DisplaysAlerts from "mixins/DisplaysAlerts";
import Str from "helpers/Str";

export default {
	mixins: [DisplaysAlerts],
	props: {
		user: {type: Object, required: true},
	},
	data() {
		return {
			readonly: true,
		}
	},
	computed: {
		formAction() {
			return !this.user.id
				? this.route("admin.users.store")
				: this.route("admin.users.update", {
					user: this.user.id,
				});
		},
		formMethod() {
			return !this.user.id ? "POST" : "PUT";
		},
		isEditable() {
			return !this.user.id || !!this.user.is_editable;
		},
		canEditUsername() {
			return !this.user.hvac_id && !this.user.is_writein;
		},
		canEditEmail() {
			return !this.user.hvac_id || this.user.first_name === 'Universal';
		},
		canViewUsernameOrEmail() {
			return this.$root.isSuperUser;
		},
		isPortrayable() {
			return (
				this.$root.isSuperUser &&
				!!this.user.id &&
				this.isActive &&
				!this.isWritein
			);
		},
		isActive() {
			return !!this.user.is_active;
		},
		isWritein() {
			return !!this.user.is_writein;
		},
		isTrainer() {
			return !!this.user.trainer_type;
		},
		selectedCompany() {
			if (!!this.user.company) {
				var label = this.user.company.name;
				label += !!this.user.company.hvac_id
					? ` (HVAC ID: ${this.user.company.hvac_id})`
					: ` (MLC ID: ${this.user.company.id})`;
				return {label: label, value: this.user.company.id};
			}
			return null;
		},
		adminAccessOptions() {
			let options = [];
			options.push({label: "None", value: 0});
			options.push({
				label: "Administrator",
				value: this.$root.constants.ADMIN_USER,
			});
			if (this.$root.isSuperUser) {
				options.push({
					label: "Super User",
					value: this.$root.constants.SUPER_USER,
				});
			}
			return options;
		},
		userType() {
			var type = this.metaValue("user_type");
			return !!type ? "HVAC " + Str.ucfirst(type) : "N/A";
		},
		additionalBrandedAccess() {
			var value = parseInt(this.metaValue("additional_access"), 10);
			var reason = this.metaValue("additional_access_reason");
			if (!isNaN(value) && value > 0) {
				return "Yes - " + (reason || "no reason provided by user");
			} else {
				return "No";
			}
		},
		showAviatorId() {
			return this.$root.isSuperUser || !!this.user.aviator_id;
		},
		allowEmailEdit() {
			return this.isWritein || !!this.user.is_icp_dealer;
		},
		isPublicUser() {
			return this.user.access_level == this.$root.constants.PUBLIC_USER;
		}
	},
	methods: {
		removeReadonly() {
			this.readonly = false;
		},
		metaValue(key) {
			if (typeof this.user.meta_values === "undefined") {
				return "";
			}
			return this.user.meta_values[key] || "";
		},
		showHoursModal() {
			this.$modal("HoursModal", {user: this.user})
				.then(() => {
				})
				.catch(() => {
				});
		},
	},
	mounted() {
		window.setTimeout(this.removeReadonly, 3000)
	},
};
</script>

<style lang="scss" scoped>
hr {
	margin-top: 2rem;
	margin-bottom: 2rem;
	border-width: 2px;
}
</style>